import { useCallback, useState } from "react";
import { apiFetch } from "../../../lib/api/client";

type SecurityLevel = "weak" | "medium" | "strong" | "very-strong";

function getSecurityLevel(wordCount: number): SecurityLevel {
  if (wordCount < 4) return "weak";
  if (wordCount === 4) return "medium";
  if (wordCount <= 6) return "strong";
  return "very-strong";
}

const LEVEL_CONFIG: Record<SecurityLevel, { label: string; color: string; barWidth: string }> = {
  weak: { label: "Faible", color: "text-red-500", barWidth: "w-1/4" },
  medium: { label: "Correct", color: "text-amber-500", barWidth: "w-2/4" },
  strong: { label: "Fort", color: "text-emerald-500", barWidth: "w-3/4" },
  "very-strong": { label: "Tres fort", color: "text-emerald-600", barWidth: "w-full" },
};

const BAR_COLORS: Record<SecurityLevel, string> = {
  weak: "bg-red-500",
  medium: "bg-amber-500",
  strong: "bg-emerald-500",
  "very-strong": "bg-emerald-600",
};

interface PassphraseGuideProps {
  readonly wordCount: number;
  readonly onGenerate: (words: string[]) => void;
}

export function PassphraseGuide({ wordCount, onGenerate }: PassphraseGuideProps) {
  const level = getSecurityLevel(wordCount);
  const config = LEVEL_CONFIG[level];
  const [loading, setLoading] = useState(false);

  const handleGenerate = useCallback(async (count: number) => {
    setLoading(true);
    try {
      const { words } = await apiFetch<{ words: string[] }>(
        `/users/generate-passphrase?words=${count}`,
      );
      onGenerate(words);
    } catch {
      // Silently fail — button stays available to retry
    } finally {
      setLoading(false);
    }
  }, [onGenerate]);

  return (
    <div className="flex flex-col gap-3">
      {/* Security indicator */}
      {wordCount > 0 && (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <span className="text-xs text-ink-muted">Securite</span>
            <span className={`text-xs font-medium ${config.color}`}>{config.label}</span>
          </div>
          <div className="h-1.5 bg-border rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-300 ${BAR_COLORS[level]} ${config.barWidth}`} />
          </div>
        </div>
      )}

      {/* Quick generate buttons */}
      <div className="flex flex-col gap-2">
        <span className="text-xs text-ink-muted">Generer automatiquement :</span>
        <div className="flex gap-2">
          {[4, 5, 6].map((count) => (
            <button
              key={count}
              type="button"
              disabled={loading}
              onClick={() => void handleGenerate(count)}
              className={`flex-1 px-2 py-1.5 text-xs rounded border transition-colors disabled:opacity-40 ${
                count === 4
                  ? "border-amber-500/30 text-amber-600 hover:bg-amber-500/10"
                  : count === 5
                    ? "border-emerald-500/30 text-emerald-600 hover:bg-emerald-500/10"
                    : "border-emerald-600/30 text-emerald-700 hover:bg-emerald-600/10"
              }`}
            >
              {count} mots
            </button>
          ))}
        </div>
      </div>

      {/* Tips */}
      <div className="bg-surface-alt rounded-lg p-3 flex flex-col gap-1.5">
        <span className="text-xs font-medium text-ink">Conseils</span>
        <ul className="text-[11px] text-ink-muted leading-relaxed flex flex-col gap-1">
          <li>
            Choisissez des mots <span className="text-ink">sans lien entre eux</span> pour plus de sécurité
          </li>
          <li>
            Évitez les phrases connues comme <span className="text-ink-muted italic">il était une fois</span>
          </li>
        </ul>
      </div>
    </div>
  );
}
